import { get, post, put, del } from '../../api-config';
import { gdpService, getLocale } from './_service';

// Groups
export const getGroups = async () => await get(gdpService, '/groups', null, null);
export const getGroupsTreeForm = async () => await get(gdpService, '/groups/tree', null, null);
export const getGroupsStyle = (groupId: any) => get(gdpService, `/groups/${groupId}/style`, null, null);

// Logo del grupo: efectivo (propio, heredado o el de TMC), subida y vuelta a heredar.
// Con FormData axios pone él solo el multipart/form-data con su boundary: no fijar Content-Type.
export const getGroupLogo = (groupId: string) => get(gdpService, `/groups/${groupId}/logo`, null, null);
export const putGroupLogo = (groupId: string, file: File) => {
  const form = new FormData();
  form.append('file', file);
  return put(gdpService, `/groups/${groupId}/logo`, form, null);
};
export const deleteGroupLogo = (groupId: string) => del(gdpService, `/groups/${groupId}/logo`);

// Colores de marca del grupo (se heredan aparte del logo).
export const getGroupColors = (groupId: string) => get(gdpService, `/groups/${groupId}/colors`, null, null);
export const putGroupColors = (groupId: string, colors: { primaryColor: string; secondaryColor: string }) =>
  put(gdpService, `/groups/${groupId}/colors`, colors, null);
export const deleteGroupColors = (groupId: string) => del(gdpService, `/groups/${groupId}/colors`);

// Servicios de salud del grupo: propios + heredados de ancestros (inherited, solo lectura).
// DELETE archiva; para desarchivar, PUT con archived: false.
export const getGroupHealthServices = (groupId: string, includeArchived = false) =>
  get(gdpService, `/groups/${groupId}/health-services?includeArchived=${includeArchived}`, null, null);
export const postGroupHealthService = (groupId: string, data: any) =>
  post(gdpService, `/groups/${groupId}/health-services`, data, null);
export const putGroupHealthService = (groupId: string, id: string, data: any) =>
  put(gdpService, `/groups/${groupId}/health-services/${id}`, data, null);
export const archiveGroupHealthService = (groupId: string, id: string) =>
  del(gdpService, `/groups/${groupId}/health-services/${id}`);

// Contactos generales del grupo: propios + heredados (inherited). DELETE archiva; para desarchivar, PUT con
// archived: false.
export const getGroupGeneralContacts = (groupId: string, includeArchived = false) =>
  get(gdpService, `/groups/${groupId}/general-contacts?includeArchived=${includeArchived}`, null, null);
export const postGroupGeneralContact = (groupId: string, data: any) =>
  post(gdpService, `/groups/${groupId}/general-contacts`, data, null);
export const putGroupGeneralContact = (groupId: string, id: string, data: any) =>
  put(gdpService, `/groups/${groupId}/general-contacts/${id}`, data, null);
export const archiveGroupGeneralContact = (groupId: string, id: string) =>
  del(gdpService, `/groups/${groupId}/general-contacts/${id}`);

// Listas de selección rápida del grupo por tipo (PATHOLOGY, INTERVENTION): propias + heredadas (inherited).
// DELETE archiva; para desarchivar, PUT con archived: false. move cambia el orden dentro del grupo.
export const getGroupQuickSelections = (groupId: string, type: string, includeArchived = false) =>
  get(gdpService, `/groups/${groupId}/quick-selections?type=${type}&includeArchived=${includeArchived}`, null, null);
export const postGroupQuickSelection = (groupId: string, data: any) =>
  post(gdpService, `/groups/${groupId}/quick-selections`, data, null);
export const putGroupQuickSelection = (groupId: string, id: string, data: any) =>
  put(gdpService, `/groups/${groupId}/quick-selections/${id}`, data, null);
export const moveGroupQuickSelection = (groupId: string, id: string, direction: 'up' | 'down') =>
  put(gdpService, `/groups/${groupId}/quick-selections/${id}/move?direction=${direction}`, null, null);
export const archiveGroupQuickSelection = (groupId: string, id: string) =>
  del(gdpService, `/groups/${groupId}/quick-selections/${id}`);

// Group detail
export const getGroup = (id: any) => get(gdpService, `/groups/${id}`, null, null);
export const postGroup = (data: any) => post(gdpService, `/groups`, data, null);
export const putGroup = (data: any) => put(gdpService, `/groups`, data, null);
export const getGroupMessage = () => get(gdpService, `/group-message`, null, null);

// Resync de pacientes hacia SAC. El backend lo procesa en segundo plano: la respuesta trae
// cuántos pacientes se han encolado, no cuántos ha aplicado SAC.
export const postGroupSacResync = (groupId: any) => post(gdpService, `/groups/${groupId}/sac/resync`, {}, null);

// Comandos remotos a nivel de grupo (versión masiva de devices/{id}/command). El backend acepta
// cualquier acción; el catálogo de comandos ofrecidos vive en el front (RemoteCommands/commands.ts).
// `targets` es la previsualización: dice a cuántos dispositivos afectaría sin publicar nada.
//
// Los tipos van en CSV, no como array: el helper `post` no lleva paramsSerializer, así que axios
// mandaría `deviceTypes[]=...` y el `@RequestParam List<String>` de Spring no lo bindea. Un valor
// separado por comas sí lo convierte a lista, y sirve igual para el GET.
const toCsv = (value: any) => (Array.isArray(value) ? value.join(',') : value) || undefined;

export const getGroupDevicesCommandTargets = (groupId: any, params: any) =>
  get(gdpService, `/groups/${groupId}/devices/command/targets`, null, {
    ...params,
    deviceTypes: toCsv(params?.deviceTypes),
  });
export const postGroupDevicesCommand = (groupId: any, params: any) =>
  post(gdpService, `/groups/${groupId}/devices/command`, {}, { ...params, deviceTypes: toCsv(params?.deviceTypes) });

// Group rules (measures)
export const getMeasuresGroupRules = (id: any) => get(gdpService, `/measures/groupRules`, null, { id });
export const postMeasuresGroupRules = (data: any) => post(gdpService, `/measures/groupRules`, data, null);
export const putMeasuresGroupRules = (data: any) => put(gdpService, `/measures/groupRules/`, data, null);
export const deleteMeasuresGroupRules = (path: any) => del(gdpService, `/measures/groupRules/${path}`);

// Group service numbers
export const postGroupServiceNumbers = (data: any) =>
  post(gdpService, `/groups/${data.groupId}/service_numbers`, data, null);
export const putGroupServiceNumbers = (data: any) =>
  put(gdpService, `/groups/${data.groupId}/service_numbers`, data, null);

// Group notifications
export const getGroupNotifications = (data: any) =>
  get(gdpService, `/groups/${data.id}/notifications`, null, { init: data.init, size: data.size });
export const postGroupNotification = (data: any) =>
  post(gdpService, `/groups/${data.id}/notifications`, data, null);
export const putGroupNotification = (data: any) =>
  put(gdpService, `/groups/${data.groupId}/notifications/${data.id}`, data, null);
export const putGroupNotificationSend = (data: any) =>
  put(gdpService, `/groups/${data.groupId}/notifications/${data.id}/send`, data, { sentToSubgroups: false });
export const deleteGroupNotification = (data: any) =>
  del(gdpService, `/groups/${data.groupId}/notifications/${data.id}`);

// Group service-state alert config
export const getGroupServiceStateAlertConfig = (groupId: any) =>
  get(gdpService, `/groups/${groupId}/serviceStateAlertConfig`, null, null);
export const postGroupServiceStateAlertConfig = (groupId: any, data: any) =>
  post(gdpService, `/groups/${groupId}/serviceStateAlertConfig`, data, null);
export const putGroupServiceStateAlertConfig = (groupId: any, id: any, data: any) =>
  put(gdpService, `/groups/${groupId}/serviceStateAlertConfig/${id}`, data, null);
export const deleteGroupServiceStateAlertConfig = (groupId: any, id: any) =>
  del(gdpService, `/groups/${groupId}/serviceStateAlertConfig/${id}`);

// Not-measured supervisor: config por grupo (falta de medida)
export const getNotMeasuredConfig = (groupId: any) =>
  get(gdpService, `/groups/${groupId}/notMeasuredConfig`, null, null);
export const postNotMeasuredConfig = (groupId: any, data: any) =>
  post(gdpService, `/groups/${groupId}/notMeasuredConfig`, data, null);
export const putNotMeasuredConfig = (groupId: any, id: any, data: any) =>
  put(gdpService, `/groups/${groupId}/notMeasuredConfig/${id}`, data, null);

// Not-measured supervisor: ajustes globales (singleton) y mapeo global de dispositivos (solo lectura)
export const getNotMeasuredSettings = () => get(gdpService, `/notMeasuredSupervisorSettings`, null, null);
export const putNotMeasuredSettings = (data: any) => put(gdpService, `/notMeasuredSupervisorSettings`, data, null);
export const getNotMeasuredDeviceTypeMap = () => get(gdpService, `/notMeasuredDeviceTypeMap`, null, null);

// Tipos de servicio de un grupo (para el desplegable de reglas)
export const getServiceTypesForGroup = (groupId: any) =>
  get(gdpService, `/groups/${groupId}/service-types`, null, null);

// Administración de tipos de servicio del grupo: alta, edición y archivado, más la permanencia que
// determina la fecha prevista de baja. Se piden con includeArchived para poder reactivarlos; el resto de
// pantallas usa getServiceTypesForGroup, que solo trae los activos.
// No hay borrado: un tipo en uso no se puede borrar (FK de PATIENT_SERVICE_TYPE) y borrar uno libre se
// llevaría en cascada sus periféricos y su regla de falta de medida.
export const getGroupServiceTypesAdmin = (groupId: any) =>
  get(gdpService, `/groups/${groupId}/service-types`, null, { includeArchived: true, locale: getLocale() });
export const postGroupServiceType = (groupId: any, data: any) =>
  post(gdpService, `/groups/${groupId}/service-types`, data, null);
export const putGroupServiceType = (groupId: any, id: any, data: any) =>
  put(gdpService, `/groups/${groupId}/service-types/${id}`, data, null);
export const archiveGroupServiceType = (groupId: any, id: any) =>
  del(gdpService, `/groups/${groupId}/service-types/${id}`);
export const restoreGroupServiceType = (groupId: any, id: any) =>
  put(gdpService, `/groups/${groupId}/service-types/${id}/restore`, {}, null);

// Configuración por grupo de la alerta de revisión del servicio (si avisa, con cuánta antelación y con qué
// prioridad en la cola). `resolved` trae el valor efectivo con la herencia ya resuelta y de qué grupo sale;
// el GET raíz, la fila propia (item null = el grupo hereda). El DELETE la devuelve a heredar.
export const getGroupServiceReviewConfig = (groupId: any) =>
  get(gdpService, `/groups/${groupId}/service-review-config`, null, null);
export const getGroupServiceReviewConfigResolved = (groupId: any) =>
  get(gdpService, `/groups/${groupId}/service-review-config/resolved`, null, null);
export const putGroupServiceReviewConfig = (groupId: any, data: any) =>
  put(gdpService, `/groups/${groupId}/service-review-config`, data, null);
export const deleteGroupServiceReviewConfig = (groupId: any) =>
  del(gdpService, `/groups/${groupId}/service-review-config`);

// Not-measured supervisor: reglas (umbral) por tipo de servicio
export const getNotMeasuredServiceRules = (groupId: any) =>
  get(gdpService, `/groups/${groupId}/notMeasuredServiceRules`, null, null);
export const postNotMeasuredServiceRule = (groupId: any, data: any) =>
  post(gdpService, `/groups/${groupId}/notMeasuredServiceRules`, data, null);
export const putNotMeasuredServiceRule = (groupId: any, id: any, data: any) =>
  put(gdpService, `/groups/${groupId}/notMeasuredServiceRules/${id}`, data, null);
export const deleteNotMeasuredServiceRule = (groupId: any, id: any) =>
  del(gdpService, `/groups/${groupId}/notMeasuredServiceRules/${id}`);

// Not-measured supervisor: relevancia de tipos de dispositivo por grupo (mapeo global + override)
export const getNotMeasuredDeviceTypes = (groupId: any) =>
  get(gdpService, `/groups/${groupId}/notMeasuredDeviceTypes`, null, null);
export const putNotMeasuredDeviceType = (groupId: any, deviceTypeId: any, data: any) =>
  put(gdpService, `/groups/${groupId}/notMeasuredDeviceTypes/${deviceTypeId}`, data, null);
export const deleteNotMeasuredDeviceType = (groupId: any, deviceTypeId: any) =>
  del(gdpService, `/groups/${groupId}/notMeasuredDeviceTypes/${deviceTypeId}`);

// Group required-fields (validación de campos mínimos para evolucionar el estado del servicio,
// con herencia por jerarquía de grupos: gana el ancestro más cercano con configuración propia)
export const getGroupRequiredFieldsResolved = (groupId: any) =>
  get(gdpService, `/groups/${groupId}/required-fields/resolved`);
export const getGroupRequiredFieldsCatalog = (groupId: any, locale?: any) =>
  get(gdpService, `/groups/${groupId}/required-fields/catalog`, null, locale ? { locale } : null);
export const getGroupRequiredFields = (groupId: any) => get(gdpService, `/groups/${groupId}/required-fields`);
export const putGroupRequiredFields = (groupId: any, data: any) =>
  put(gdpService, `/groups/${groupId}/required-fields`, data);
export const deleteGroupRequiredFields = (groupId: any) => del(gdpService, `/groups/${groupId}/required-fields`);

// Integración FHIR/TSV por grupo (pestaña de Integración, solo superadmin; herencia por jerarquía
// de grupos: gana el ancestro más cercano con configuración propia, DELETE = volver a heredar)
export const getGroupFhirConfig = (groupId: any) => get(gdpService, `/groups/${groupId}/fhir-config`);
export const putGroupFhirConfig = (groupId: any, data: any) => put(gdpService, `/groups/${groupId}/fhir-config`, data);
export const deleteGroupFhirConfig = (groupId: any) => del(gdpService, `/groups/${groupId}/fhir-config`);
export const getGroupMeasureSyncConfig = (groupId: any) => get(gdpService, `/groups/${groupId}/measure-sync-config`);
export const putGroupMeasureSyncConfig = (groupId: any, data: any) =>
  put(gdpService, `/groups/${groupId}/measure-sync-config`, data);
export const deleteGroupMeasureSyncConfig = (groupId: any) => del(gdpService, `/groups/${groupId}/measure-sync-config`);

// Group ui-visibility (configuración de visibilidad de UI por grupo, con herencia desde la raíz del árbol)
// El token lo inyecta el interceptor (api-config.ts), no hace falta pasarlo.
export const getGroupUiVisibilityResolved = (groupId: any) => get(gdpService, `/groups/${groupId}/ui-visibility/resolved`);
export const getGroupUiVisibility = (groupId: any) => get(gdpService, `/groups/${groupId}/ui-visibility`);
export const putGroupUiVisibility = (groupId: any, data: any) => put(gdpService, `/groups/${groupId}/ui-visibility`, data);
export const deleteGroupUiVisibility = (groupId: any) => del(gdpService, `/groups/${groupId}/ui-visibility`);
